import React from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import {
  Button,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import {
  authUrls,
  // getServiceURL,
  SERVICES_URL,
  useQueryUser,
  useTranslation
} from '@plandi/common'
import { GetServerSidePropsContext } from 'next'
import { QueryClient } from 'react-query'
import { TRANSLATE, sharedApi } from 'src/shared'
import { dehydrate } from 'react-query/hydration'
import {
  BreadCrumbs,
  FilterSearchBar,
  FilterSearchBarMobile,
  MainLayout,
  SORT_KEY_NEW,
  SORT_KEY_OLD,
  SORT_KEY_RATING,
  TextFieldParamsSort
} from 'src/features'
import {
  BlogArticle,
  BlogArticleSkeleton,
  PaginationBlock,
  TabsBlog,
  mainSeo,
  mainSeoEn,
  useQueryArticlesList,
  useQueryArticlesRubrics
} from 'src/common'
import { URLS } from 'src/router/urls'

export const getServerSideProps = async ({
  locale
}: GetServerSidePropsContext) => {
  const queryClient = new QueryClient()

  await queryClient.prefetchQuery(TRANSLATE, () =>
    sharedApi.getTranslate(locale)
  )
  return {
    props: {
      locale: locale,
      dehydratedState: dehydrate(queryClient)
    }
  }
}

const Articles = (props: { locale: string }) => {
  const url = SERVICES_URL
  const locale = props.locale

  const { data: articles, isLoading, isError } = useQueryArticlesList()
  const { data: rubrics } = useQueryArticlesRubrics()
  const { data: user } = useQueryUser()
  const { data: translate } = useTranslation()

  const { title, description } = mainSeo
  const { titleEn, descriptionEn } = mainSeoEn

  const router = useRouter()

  const redirect =
    authUrls(locale).signIn +
    `?redirectUrl=${
      locale === 'ru'
        ? `${url.blog}/ru/articles/create`
        : url.blog + '/articles/create'
    }`

  const handlePage = (page: number) => {
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          page
        }
      },
      '/articles',
      { shallow: false }
    )
  }
  const theme = useTheme()

  const isTablet = useMediaQuery(theme.breakpoints.down(960))
  const isMobile = useMediaQuery(theme.breakpoints.down(600))

  return (
    <MainLayout
      withFooter
      title={props.locale === 'ru' ? title : titleEn}
      description={props.locale === 'ru' ? description : descriptionEn}
      mainContainerStyles={{ display: 'initial' }}
    >
      <Grid container spacing={4}>
        <Grid
          item
          xs={12}
          style={{ paddingBottom: isTablet ? 50 : 0, height: 50 }}
        >
          <BreadCrumbs
            breadcrumbInfo={[
              {
                title: translate?.blog ?? 'Блог',
                href:
                  locale === 'ru'
                    ? `${url.blog}/ru/articles`
                    : `${url.blog}/articles`
              },
              {
                title:
                  translate?.['blog-site']?.['blog-articles'] ?? 'Статьи блога',
                href: ''
              }
            ]}
          />
        </Grid>
        <Grid item xs={12} style={{ padding: 0 }}>
          <TabsBlog locale={props.locale} place="articles" />
        </Grid>
        <Grid
          item
          container
          xs={12}
          justifyContent="space-between"
          alignItems="center"
          style={{ paddingTop: 0 }}
        >
          <Grid item xs={12} md={3}>
            {isTablet ? (
              <FilterSearchBarMobile
                rubrics={rubrics}
                isLoading={isLoading}
                typeForSearch={'allArticles'}
              />
            ) : (
              <FilterSearchBar
                isLoading={isLoading}
                typeForSearch={'allArticles'}
                modalStyles={{
                  borderRadius: 13,
                  boxShadow: '0px 4px 25px 0px rgba(0, 0, 0, 0.10)'
                }}
              />
            )}
          </Grid>
          {!isTablet && (
            <Grid item container xs={12} md={6} spacing={4}>
              <Grid item xs={6} style={{ paddingTop: 0 }}>
                <TextFieldParamsSort
                  isLoading={isLoading}
                  sortKeysRubrics={rubrics}
                  queryKey="rubric"
                  defaultValue={(rubrics && rubrics[0]?.id) ?? ''}
                  typeForSearch={'allArticles'}
                  translate={translate}
                  variant="outlined"
                  rubrics
                />
              </Grid>
              <Grid item xs={6} style={{ paddingTop: 0 }}>
                <TextFieldParamsSort
                  isLoading={isLoading}
                  sortKeys={[SORT_KEY_OLD, SORT_KEY_NEW, SORT_KEY_RATING]}
                  queryKey="sort"
                  defaultValue={SORT_KEY_NEW}
                  typeForSearch={'allArticles'}
                  translate={translate}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid
          item
          container
          xs={12}
          direction={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isMobile ? 'flex-start' : 'center'}
          style={{ paddingTop: isMobile ? 0 : 16 }}
        >
          <Typography
            style={{
              color: '#757575',
              marginBottom: isMobile ? 20 : 0,
              alignSelf: isMobile ? 'flex-start' : 'center'
            }}
            variant="body2"
          >
            {articles?.data?.length === 0 ? null : (
              <>
                {isLoading || isError
                  ? null
                  : `${
                      translate?.['blog-site']?.['total-articles'] ??
                      'Всего статей:'
                    } ${articles?.total}`}
              </>
            )}
          </Typography>
          <Link href={user ? URLS.articles.create : redirect} passHref>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              color="secondary"
              style={{
                textTransform: 'none',
                width: 'fit-content',
                fontSize: isTablet ? 12 : 14,
                fontWeight: 600
              }}
            >
              {translate?.['blog-site']?.['post-article'] ??
                'Разместить статью'}
            </Button>
          </Link>
        </Grid>
        {isLoading ? (
          [...new Array(6)].map((_, i) => <BlogArticleSkeleton key={i} />)
        ) : isError || (articles?.data ?? [])?.length < 1 ? (
          <Typography
            variant="h5"
            style={{
              textAlign: 'center',
              margin: '150px 16px 100px 16px',
              width: '100%'
            }}
          >
            {isError
              ? translate?.['blog-site']?.['no-articles'] ??
                'На данный момент статей нет'
              : translate?.['not-found-data'] ??
                'Ничего не найдено, попробуйте повторить поиск с другими параметрами'}
          </Typography>
        ) : (
          articles?.data?.map((article) => {
            return (
              <BlogArticle
                article={article}
                key={article.id}
                translate={translate}
                locale={props.locale}
              />
            )
          })
        )}
        <Grid
          item
          xs={12}
          style={{ margin: isTablet ? '60px 0 70px 0' : '110px 0 120px 0' }}
        >
          <PaginationBlock
            isLoading={isLoading}
            centered
            page={Number(router.query.page || 1)}
            handleChange={handlePage}
            pageCount={articles?.lastPage as number}
            hidden={isError || articles?.data?.length === 0}
          />
        </Grid>
      </Grid>
    </MainLayout>
  )
}
export default Articles
